import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, Plane, Star, ArrowLeft } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import Seo from "@/components/Seo";
import { useContent } from "@/lib/content-context";

export default function DestinationDetailPage() {
  const { slug } = useParams();
  const { destinations } = useContent();
  const d = destinations.find((x) => x.slug === slug);
  if (!d) return <Navigate to="/destinations" replace />;

  const idx = destinations.findIndex((x) => x.slug === slug);
  const next = destinations[(idx + 1) % destinations.length];
  const prev = destinations[(idx - 1 + destinations.length) % destinations.length];

  return (
    <div data-testid={`destination-page-${d.slug}`}>
      <Seo
        title={`${d.name} (${d.iata}) — Flights from the UK with Air Zimbabwe | Simba Travel`}
        description={`Fly to ${d.name}, ${d.country} with Air Zimbabwe. Destination overview, best time to visit, key attractions and a direct enquiry form — ticketed via Simba Travel, the UK GSA.`}
        image={d.hero}
      />
      {/* HERO */}
      <section className="relative h-[70vh] min-h-[500px] -mt-24 flex items-end overflow-hidden">
        <img src={d.hero} alt={d.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/40 to-midnight/10" />
        <div className="relative container-edge pb-14 text-linen">
          <Link to="/destinations" className="inline-flex items-center gap-2 text-linen/70 hover:text-ochre text-xs tracking-widest uppercase mb-6">
            <ArrowLeft className="w-4 h-4" /> All destinations
          </Link>
          <div className="overline text-ochre mb-4">{d.country} · {d.iata}</div>
          <h1 className="editorial-h1">{d.name}</h1>
        </div>
      </section>

      {/* CONTENT 2-col */}
      <section className="section-pad bg-linen">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Sticky sidebar */}
          <aside className="md:col-span-4">
            <div className="md:sticky md:top-28">
              <div className="border border-ink/15 bg-sand p-8">
                <div className="overline text-terracotta">Quick facts</div>
                <div className="mt-6 space-y-5">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 stroke-1 text-terracotta flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted2">Country</div>
                      <div className="font-serif text-xl">{d.country}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Plane className="w-5 h-5 stroke-1 text-terracotta flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted2">Airport code</div>
                      <div className="font-serif text-xl">{d.iata}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 stroke-1 text-terracotta flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted2">Best time to visit</div>
                      <div className="text-sm mt-1 leading-relaxed">{d.bestTime}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-ink/15 bg-midnight text-linen p-8 mt-6">
                <div className="overline text-ochre">Flight information</div>
                <p className="text-sm text-linen/80 mt-4 leading-relaxed">{d.flightInfo}</p>
                <Link to="/contact" className="btn-accent mt-6 w-full" data-testid="dest-book-cta">
                  Enquire to book <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </aside>

          {/* Long-form */}
          <article className="md:col-span-8">
            <div className="overline text-terracotta mb-4">Destination overview</div>
            <h2 className="editorial-h2">{d.name} — a city of contrasts.</h2>
            <p className="mt-6 text-lg text-ink/80 leading-relaxed font-light">{d.overview}</p>

            <div className="mt-14">
              <div className="overline text-terracotta mb-4">Travel guide</div>
              <p className="text-base text-muted2 leading-relaxed">{d.guide}</p>
            </div>

            <div className="mt-14">
              <div className="overline text-terracotta mb-4">Key attractions</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-ink/10 border border-ink/10 mt-4">
                {d.attractions.map((a) => (
                  <div key={a} className="bg-linen p-5 flex items-start gap-3">
                    <Star className="w-4 h-4 stroke-1 text-terracotta flex-shrink-0 mt-1" />
                    <span className="text-sm">{a}</span>
                  </div>
                ))}
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* ENQUIRY */}
      <section className="section-pad bg-sand">
        <div className="container-edge max-w-3xl">
          <EnquiryForm
            defaultType="destination"
            title={`Enquire about ${d.name}`}
            subtitle="Tell us about your travel dates, party size and any special requirements."
            hiddenContext={{ destination: d.name, subject: `Destination enquiry: ${d.name}` }}
            testIdPrefix={`dest-${d.slug}-enquiry`}
          />
        </div>
      </section>

      {/* PREV/NEXT */}
      <section className="bg-linen border-t border-ink/10">
        <div className="container-edge py-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-6">
          <Link to={`/destinations/${prev.slug}`} className="group flex items-center gap-4" data-testid="dest-prev">
            <ArrowLeft className="w-4 h-4 stroke-1 group-hover:-translate-x-1 transition-transform" />
            <div>
              <div className="overline">Previous</div>
              <div className="font-serif text-xl">{prev.name}</div>
            </div>
          </Link>
          <Link to={`/destinations/${next.slug}`} className="group flex items-center gap-4 sm:text-right" data-testid="dest-next">
            <div>
              <div className="overline">Next</div>
              <div className="font-serif text-xl">{next.name}</div>
            </div>
            <ArrowRight className="w-4 h-4 stroke-1 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
